import { apiClient } from "./apiService";
import { DeploymentObject } from "../modules/requests/ProjectRequestForm";
import Logger from "../utils/logger";

export interface RepoGroup {
  group: string;
  repo: string[];
  dbRepo: string[];
}

export interface RepoData {
  repos: RepoGroup[];
}

export interface FlattenedRepo {
  repoName: string;
  groupName: string;
}

export interface DeploymentTable {
  [repoName: string]: {
    [envName: string]: {
      branch: string;
      timestamp: string;
      deployed_by: string;
      db_triggered: boolean;
      inProgress: boolean;
      status: string;
    } | null;
  };
}

export interface Environment {
  name: string;
  label: string;
}

export interface Deployment {
  repo: string;
  env: string;
  branch: string;
  timestamp: string;
  deployed_by: string;
  db_triggered: boolean;
  inProgress: boolean;
  status: string;
}

export function flattenArray(input: RepoData): FlattenedRepo[] {
  Logger.info("Flattening repo array", { repoData: input });
  const flattened: FlattenedRepo[] = [];

  for (const repoGroup of input.repos) {
    for (const repoName of repoGroup.repo) {
      flattened.push({
        repoName: repoName,
        groupName: repoGroup.group,
      });
    }
  }

  Logger.info("Flattened repo array successfully", { flattened });
  return flattened;
}

export async function getDeploymentData() {
  try {
    Logger.info("Fetching deployment data");
    const { data } = await apiClient.get("/deployments");
    Logger.info("Fetched deployment data successfully", { data });
    return data;
  } catch (error) {
    Logger.error("Failed to fetch deployment data", { error });
    throw error;
  }
}

export async function getRepos() {
  try {
    Logger.info("Fetching repos");
    const { data } = await apiClient.get("/repos");
    Logger.info("Fetched repos successfully", { data });
    return flattenArray(data);
  } catch (error) {
    Logger.error("Failed to fetch repos", { error });
    throw error;
  }
}

const transformRepoFormat = (
  data: DeploymentObject
): { result: RepoGroup; isValid: boolean } => {
  Logger.info("Transforming repo format", { data });

  if (!data.group || !Array.isArray(data.repo) || !Array.isArray(data.dbRepo)) {
    Logger.warn("Invalid input data format", { data });
    return { result: { group: "", repo: [], dbRepo: [] }, isValid: false };
  }

  const result: RepoGroup = {
    group: data.group
      .trim()
      .replace(/\s+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, ""),
    repo: data.repo
      .filter((r) => r.name && r.type) // Ensure name and type exist
      .map(
        (r) =>
          r.name
            .trim()
            .replace(/\s+/g, "-")
            .replace(/^-+/, "")
            .replace(/-+$/, "") + `-${r.type}`
      ),
    dbRepo: data.dbRepo
      .filter((db) => db.name && db.type) // Ensure name and type exist
      .map(
        (db) =>
          db.name
            .trim()
            .replace(/\s+/g, "-")
            .replace(/^-+/, "")
            .replace(/-+$/, "") + `-${db.type}`
      ),
  };

  const isValid =
    !!result.group &&
    result.repo.length > 0 &&
    (data.dbRepo.length === 0 || result.dbRepo.length > 0);

  Logger.info("Repo format transformed", { result, isValid });
  return { result, isValid };
};

export async function addRepos(repoDetails: DeploymentObject) {
  Logger.info("Adding new repo", { repoDetails });
  const { result: repoGroup, isValid: isValidRepoGroup } =
    transformRepoFormat(repoDetails);

  if (
    !isValidRepoGroup ||
    !repoGroup.repo.length ||
    (repoDetails.dbRepo.length > 0 && !repoGroup.dbRepo.length)
  ) {
    Logger.warn("Invalid repo group format", { repoGroup });
    return {
      status: 400,
      message: "Failed to create repo group. Bad repo format",
    };
  }

  const dataToSend = {
    ...repoGroup,
    requester: repoDetails.requester,
  };

  try {
    const response = await apiClient.post("/request", dataToSend);
    if (!response?.data?.issueKey) {
      Logger.warn("Repo created but issueKey missing", { response });
      return { status: 500, message: "Repo creation failed unexpectedly" };
    }
    Logger.info("Repo group created successfully", {
      issueKey: response.data.issueKey,
    });
    return { status: 201, message: "Created", data: response.data.issueKey };
  } catch (error) {
    Logger.error("Failed to create repo group", { error });
    return { status: 500, message: "Failed to create repo group" };
  }
}

export async function getEnvironments() {
  try {
    Logger.info("Fetching environments");
    const { data } = await apiClient.get("/envs");
    Logger.info("Fetched environments successfully", { data });
    return data.environments;
  } catch (error) {
    Logger.error("Failed to fetch environments", { error });
    throw error;
  }
}

export function buildDeploymentTable(
  flattenedRepos: FlattenedRepo[],
  environments: Environment[],
  deployments: Deployment[]
): DeploymentTable {
  Logger.info("Building deployment table", {
    flattenedRepos,
    environments,
    deployments,
  });

  const table: DeploymentTable = {};

  for (const repo of flattenedRepos) {
    table[repo.repoName] = {};
    for (const env of environments) {
      table[repo.repoName][env.name] = null;
    }
  }

  for (const deployment of deployments) {
    const {
      repo,
      env,
      branch,
      timestamp,
      deployed_by,
      db_triggered,
      inProgress,
      status,
    } = deployment;

    if (table[repo] && table[repo][env] !== undefined) {
      table[repo][env] = {
        branch,
        timestamp,
        deployed_by,
        db_triggered,
        inProgress,
        status,
      };
    }
  }

  Logger.info("Deployment table built successfully", { table });
  return table;
}
